import type { JSX } from 'react';
import { PopoverArrow as RadixPopoverArrow } from '@radix-ui/react-popover';
import { styled, css, keyframes } from 'styled-components';

const StyledPath = styled.path`
  fill: var(--wui-color-border-secondary);
`;

const circleAnimation = keyframes`
  0% {
    opacity: var(--wui-popover-arrow-circle-starting-opacity);
  }
  100% {
    opacity: 0;
  }
`;

const StyledCircle = styled.circle<{ $isAnimated: boolean }>`
  stroke: var(--wui-color-border-active);
  animation-duration: 2s;
  animation-iteration-count: infinite;
  animation-direction: alternate;
  animation-timing-function: ease-in-out;

  &[data-wui-popover-arrow-inner-circle] {
    --wui-popover-arrow-circle-starting-opacity: 0.36;

    opacity: var(--wui-popover-arrow-circle-starting-opacity);
  }

  &[data-wui-popover-arrow-outer-circle] {
    --wui-popover-arrow-circle-starting-opacity: 0.2;

    animation-direction: alternate-reverse;
    opacity: 0.11;
  }

  @media (prefers-reduced-motion: no-preference) {
    ${({ $isAnimated }) =>
      $isAnimated &&
      css`
        animation-name: ${circleAnimation};
      `}
  }
`;

export type PopoverArrowProps = {
  /**
   * Should the arrow be animated with ripple effect
   */
  isAnimated: boolean;
};

export const PopoverArrow = ({ isAnimated }: PopoverArrowProps): JSX.Element => {
  return (
    <RadixPopoverArrow asChild={true}>
      <svg
        fill="none"
        height="56"
        viewBox="0 0 48 56"
        width="48"
        xmlns="http://www.w3.org/2000/svg"
      >
        <StyledPath d="M24 26.6667C21.0545 26.6667 18.6667 29.0545 18.6667 32C18.6667 34.9455 21.0545 37.3333 24 37.3333C26.9455 37.3333 29.3333 34.9455 29.3333 32C29.3333 29.0545 26.9455 26.6667 24 26.6667ZM23 0V32H25V0H23Z" />
        <StyledCircle
          $isAnimated={isAnimated}
          cx="24"
          cy="32"
          data-wui-popover-arrow-inner-circle={true}
          r="10"
          strokeWidth="4"
        />
        <StyledCircle
          $isAnimated={isAnimated}
          cx="24"
          cy="32"
          data-wui-popover-arrow-outer-circle={true}
          r="20"
          strokeWidth="8"
        />
      </svg>
    </RadixPopoverArrow>
  );
};

PopoverArrow.displayName = 'PopoverArrow';
